import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { styleTxn } from "../../utils/style-txn";
import { StringProgress } from "./StringProgress";

type ParallaxTransformResult = { transform: string };

/**
 * The `StringParallax` class extends the `StringProgress` class to provide
 * functionality for handling parallax effects on scrollable elements.
 * It maps specific attributes related to parallax and calculates the
 * necessary transformations based on scroll progress and viewport size.
 */
export class StringParallax extends StringProgress {
  private updateScheduledTransform = false;
  private calculateParallaxForObject: (object: StringObject) => ParallaxTransformResult | null;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "parallax";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "parallax", type: "number", fallback: this.settings["parallax"] },
      {
        key: "parallax-bias",
        type: "number",
        fallback: this.settings["parallax-bias"],
      },
    ];

    this.calculateParallaxForObject = this.calculateDesktopParallax;
  }

  /**
   * Called when an object is initialized.
   */
  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    const bias = object.getProperty<number>("parallax-bias") ?? 0.0;
    const factor = Math.abs(object.getProperty<number>("parallax") ?? 0.2);
    object.setProperty("parallax-sign", Math.sign(object.getProperty<number>("parallax")));
    object.setProperty<number>("parallax", factor);
    object.setProperty("parallax-position-start", -0.5 + 0.5 * bias);
    object.setProperty("parallax-position-end", 0.5 + 0.5 * (1 - bias));

    const screenSize = this.data.viewport.windowHeight;

    object.setProperty("offset-top", factor * screenSize);
    object.setProperty("offset-bottom", factor * screenSize);
  }

  calculatePositions(object: StringObject, windowSize: number) {
    super.calculatePositions(object, windowSize);
    object.setProperty("transform-value", this.calculateParallaxForObject(object));
  }

  override onScroll(data: StringData): void {
    super.onScroll(data);
  }

  override onResize(): void {
    const isDesktop = window.innerWidth > 1024;
    this.calculateParallaxForObject = isDesktop
      ? this.calculateDesktopParallax
      : this.calculateMobileParallax;
  }

  onScrollMeasure(data: StringData) {
    super.onScrollMeasure(data);
    this.objects.forEach((object) => {
      object.setProperty("transform-value", this.calculateParallaxForObject(object));
    });
  }

  onMutate() {
    this.objects.forEach((object) => {
      const transformData = object.getProperty<ParallaxTransformResult>("transform-value");
      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setProps(el, transformData);
      });
    });
  }

  private calculateDesktopParallax = (object: StringObject): ParallaxTransformResult => {
    const progress = object.getProperty<number>("progress") ?? 0;
    const factor = object.getProperty<number>("parallax") ?? 0;
    const start = object.getProperty<number>("parallax-position-start") ?? 0;
    const end = object.getProperty<number>("parallax-position-end") ?? 1;
    const sign = object.getProperty<number>("parallax-sign") ?? 1;

    const screenSize = this.data.viewport.windowHeight / this.data.viewport.transformScale;
    const translation = sign * factor * (screenSize * start + progress * screenSize * end);

    this.events.emit(`object:parallax:${object.id}`, translation);

    return { transform: `translate3d(0, ${translation}px, 0)` };
  };

  private calculateMobileParallax = (object: StringObject): ParallaxTransformResult => {
    this.events.emit(`object:parallax:${object.id}`, 0);
    return { transform: `translate3d(0, 0px, 0)` };
  };
}
